import React, {
	forwardRef,
	memo,
	useCallback,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
} from 'react';
import { VariableSizeList, FixedSizeList } from 'react-window';
import * as PropTypes from 'prop-types';
import {
	useObserveRef,
	isNumber,
	useDeepMemoize,
	useForceRender,
	useObservableRef,
	isEqual,
	useDebugger,
} from '@chkp/mwc-common';
import throttle from 'lodash.throttle';
import { Row } from './Row';
import { VirtualizedSizer } from './VirtualizedSizer';
import { useColumnsObservable } from './useColumns';
import { Header } from './Header';
import { useVirtualizedRows } from './useVirtualizedRows';
import { useSizing } from './useSizing';
import styles from './Grid.scss';
import clsx from 'clsx';
import { useScrollbarsOuterElementType } from './useScrollbarsOuterElementType';
import { useInnerElementType } from './useInnerElementType';

// =====================================================================================================

let Grid = (props, ref) => {
	const {
		tag,
		getRowKeyByIdx,
		rowsCount,
		rowHeight,
		rowHover,
		columns,
		rowRenderer,
		forceUpdateIdx,
		onRangeChange,
		onRowClick,
		onRowDoubleClick,
		onRowMouseDown,
		context,
		overscanCount,
		rowBorders,
		cellBorders,
		rowBackground,
		striped,
		dragTooltip: dragTooltipProp,
		onDragStart: onDragStartProp,
		onDrag: onDragProp,
		onDragEnd: onDragEndProp,
		background,
		detectHover,
		showHeader,
		store,
		outline,
		message,
		messageMinHeight,
		debug,
	} = props;

	const innerRef = useRef();
	const listRef = useRef();
	const renderedRowsRef = useRef();
	const lastSync = useRef();

	//------------------------------------------------------------
	// Sizing & scroll states are managed imperatively to avoid expensive renders.
	// Chain of reactions: total-size -> list-size -> has-scroll -> content-size

	const {
		// Inputs
		totalColumnsWidthObservable, // Unconstrained columns width
		totalRowsHeightObservable, // Unconstrained rows height
		availableWidthObservable, // Measured actual width
		availableHeightObservable, // Measured actual height

		// Outputs
		totalWidthObservable, // Unconstrained height
		totalHeightObservable, // Unconstrained width
		hasScrollXObservable, // Is horizontal scrollbar required ?
		hasScrollYObservable, // Is vertical scrollbar required ?
		contentWidthObservable, // Content actual width (after subtracting scrollbar)
		contentHeightObservable, // Content actual height (after subtracting scrollbar)
	} = useSizing();

	//------------------------------------------------------------

	const scrollXObservable = useObservableRef(0);
	const scrollYObservable = useObservableRef(0);

	const {
		getRowPosition,
		itemSize,
		// estimatedItemSize,
		resetRowHeights,
		getIndexAt,
		rowPositions,
		isVariableSize,
		totalHeight,
	} = useVirtualizedRows(rowHeight, rowsCount);

	// When message is displayed instead of rows, set a fake "total-rows-height", in order for auto-sizing to work out of the box.
	totalRowsHeightObservable.current = message ? messageMinHeight : totalHeight;

	// Update rendered row cell widths in response to column changes
	const updateCellWidths = useCallback((columns) => {
		if (innerRef.current && columns) {
			const offsetterIdx = columns.findIndex((col) => !col.locked);
			Array.from(innerRef.current.children).forEach((row) => {
				const isCustomContent =
					row.getAttribute('data-custom-content') === 'true';

				if (!isCustomContent && row.childElementCount > 1) {
					const rowIndent = parseInt(row.getAttribute('data-indent'), 10);
					// TODO: use attribute to distinguish custom rows
					Array.from(row.children).forEach((cell, idx) => {
						if (idx === offsetterIdx) {
							return;
						}
						const colIdx = idx < offsetterIdx ? idx : idx - 1;
						const compensateRowIndent = (colIdx === 0 && rowIndent) || 0;
						// eslint-disable-next-line no-param-reassign
						cell.style.width = `${
							columns[colIdx]?.finalWidth - compensateRowIndent
						}px`;
					});
				}
			});
		}
	}, []);

	const [$render, forceRender] = useForceRender();

	//-------------------------------------------------------------------
	// Columns

	const columnsObservable = useColumnsObservable(
		columns,
		contentWidthObservable,
	);

	const currentColumnsRef = useRef(columnsObservable.current);

	useObserveRef(columnsObservable, (columns) => {
		if (columns === currentColumnsRef.current) {
			// Same columns instance -> Directly update DOM
			updateCellWidths(columns);
		} else {
			// New columns instance -> re-render
			currentColumnsRef.current = columns;
			forceRender();
		}
		totalColumnsWidthObservable.current = columns.totalWidth;
	});

	// Update rendered row positions
	const updateRowPositions = () => {
		if (innerRef.current) {
			const rows = Array.from(innerRef.current.children);
			const scrollX = scrollXObservable.current;
			const contentWidth = contentWidthObservable.current || 0;
			const columns = columnsObservable.current;
			const offsetterIdx = columns && columns.findIndex((col) => !col.locked);

			rows.forEach((row) => {
				const isCustomContent =
					row.getAttribute('data-custom-content') === 'true';
				const indent = parseInt(row.getAttribute('data-indent'), 10);

				/* eslint-disable no-param-reassign */
				row.style.left = `${scrollX + indent}px`;
				row.style.width = `${contentWidth - indent}px`;
				if (!isCustomContent && columns) {
					const offsetterEl = row.children.item(offsetterIdx);
					if (offsetterEl) {
						offsetterEl.style.marginLeft = `${-scrollX}px`;
					}
				}
				/* eslint-enable no-param-reassign */
			});
		}
	};

	useObserveRef(scrollXObservable, updateRowPositions);
	useObserveRef(contentWidthObservable, updateRowPositions);

	const onClick = useCallback(
		(e) => onRowClick?.(e.currentTarget.getAttribute('aria-rowindex')),
		[onRowClick],
	);

	const onDoubleClick = useCallback(
		(e) => onRowDoubleClick?.(e.currentTarget.getAttribute('aria-rowindex')),
		[onRowDoubleClick],
	);

	const onMouseDown = useCallback(
		(e) => onRowMouseDown?.(e.currentTarget.getAttribute('aria-rowindex')),
		[onRowMouseDown],
	);

	const onRangeChangeThrottled = useMemo(
		// react-window fires several onItemsRendered() in a row. Workaround by throttling the range changes.
		() =>
			onRangeChange
				? throttle(onRangeChange, 300, { leading: false, trailing: true })
				: null,
		[onRangeChange],
	);

	const onRowsRendered = useCallback(
		(renderedRows) => {
			const { overscanStartIndex, overscanStopIndex } = renderedRows;
			onRangeChangeThrottled &&
				onRangeChangeThrottled([overscanStartIndex, overscanStopIndex]);
			renderedRowsRef.current = renderedRows;
		},
		[onRangeChangeThrottled],
	);

	const List = isVariableSize ? VariableSizeList : FixedSizeList;

	const prevPositionsRef = useRef(rowPositions);

	useLayoutEffect(() => {
		if (isVariableSize && rowsCount && renderedRowsRef.current) {
			// Stick to the first visible row, in order to prevent additional range changes after resetting row heights.

			const scrollY = scrollYObservable.current;
			const { visibleStartIndex } = renderedRowsRef.current;
			const firstVisibleRowIdx = visibleStartIndex;
			const firstVisibleRowY = (prevPositionsRef.current || rowPositions)[
				firstVisibleRowIdx
			];
			prevPositionsRef.current = rowPositions;
			const offset = firstVisibleRowY - scrollY;

			// Reset react-window positions cache without force-updating.
			// The update will be done later on during scrollToItem().

			if (isVariableSize && listRef.current) {
				listRef.current.resetAfterIndex(0, true);
				listRef.current._getItemStyle(rowsCount - 1);
			}

			if (Date.now() - (lastSync.current || 0) > 10) {
				// Prevent infinite loop of scroll synchnonizations in edge cases
				lastSync.current = Date.now();
				const newFirstVisibleRowY = rowPositions[firstVisibleRowIdx];
				const newScrollY = newFirstVisibleRowY - offset;
				if (isNumber(newScrollY)) {
					if (scrollY !== newScrollY) {
						listRef.current.scrollTo(newScrollY);
					}
				} else {
					console.error('Error synchronizing scrollbar');
				}
			} else {
				console.log('Sync skipped (frequent)');
			}
		}
	}, [isVariableSize, rowPositions, rowsCount, scrollYObservable]);

	//--------------------------------------------------------------

	const scrollToItem = useCallback((idx, position = 'start') => {
		listRef.current && listRef.current.scrollToItem(idx, position);
	}, []);

	//--------------------------------------------------------------
	// Scrollbar dragging

	const getIdxRange = useCallback(
		(scrollState) => {
			const { scrollTop, clientHeight } = scrollState;

			const firstRowIdx = getIndexAt(scrollTop);
			// todo: optimize by limiting second search to [firstRowIdx..rowsCount]
			// const lastRowIdx = 0;
			const lastRowIdx = Math.max(
				getIndexAt(scrollTop + clientHeight - 30),
				firstRowIdx,
			);
			return [firstRowIdx, lastRowIdx];
		},
		[getIndexAt],
	);

	const onDragStart = useMemo(
		() =>
			onDragStartProp &&
			((scrollState) => {
				const idxRange = getIdxRange(scrollState);
				onDragStartProp(idxRange);
			}),
		[getIdxRange, onDragStartProp],
	);

	const onDrag = useMemo(
		() =>
			onDragProp &&
			((/* scrollState */) => {
				// expensive operation. if needed, make optional
				// const idxRange = getIdxRange(scrollState);
				onDragProp(/* idxRange */);
			}),
		[onDragProp],
	);

	const onDragEnd = useMemo(
		() =>
			onDragEndProp &&
			((scrollState) => {
				// scrollState may be null if scrollbar wasn't dragged
				const idxRange = scrollState ? getIdxRange(scrollState) : null;
				onDragEndProp(idxRange);
			}),
		[getIdxRange, onDragEndProp],
	);

	const dragTooltip = useMemo(
		() =>
			dragTooltipProp &&
			((scrollState) => {
				const idxRange = getIdxRange(scrollState);
				return dragTooltipProp(idxRange, scrollState);
			}),
		[dragTooltipProp, getIdxRange],
	);

	const scrollbarsRef = useRef();
	const onScrollUpdate = useCallback(
		({ scrollLeft, scrollTop }) => {
			scrollXObservable.current = scrollLeft;
			scrollYObservable.current = scrollTop;
		},
		[scrollXObservable, scrollYObservable],
	);
	const outerElementType = useScrollbarsOuterElementType({
		ref: scrollbarsRef,
		onScrollUpdate,
		onDragStart,
		onDrag,
		onDragEnd,
		dragTooltip,
		hasScrollXObservable,
		hasScrollYObservable,
		debug,
	});

	const detachScroll = useCallback(
		() => scrollbarsRef.current?.detachScroll(),
		[],
	);

	const attachScroll = useCallback(
		(idx, position) => {
			scrollbarsRef.current && scrollbarsRef.current.attachScroll();
			isNumber(idx) && scrollToItem(idx, position);
		},
		[scrollToItem],
	);

	const innerElementType = useInnerElementType({ totalWidthObservable });

	//--------------------------------------------------------------

	// Get details about element within the grid.
	const getElementDetails = useCallback((el) => {
		const cellData = el.closest('[role=cell]')?.dataset;

		if (cellData) {
			// Cell
			const rowData = el.closest('[role=row]')?.dataset;
			return {
				columnKey: cellData.columnKey,
				rowKey: rowData?.key,
				rowIndex: rowData ? parseInt(rowData.index, 10) : undefined,
			};
		} else {
			// Header cell
			const headerData = el.closest('[role=columnheader]')?.dataset;
			if (headerData) {
				return {
					columnKey: headerData?.columnKey,
				};
			}
		}
	}, []);

	// Observable-ref which holds details about hovered item
	const hoverObservable = useObservableRef({});

	const onMouseOver = useMemo(
		() =>
			detectHover
				? (e) => {
						const hoverDetails = getElementDetails(e.target);
						if (!isEqual(hoverDetails, hoverObservable.current)) {
							hoverObservable.current = hoverDetails;
						}
				  }
				: undefined,
		[detectHover, getElementDetails, hoverObservable],
	);

	// Catch mouse-leave event on the whole datagrid to discard hover-details
	const onMouseLeave = useMemo(
		() =>
			detectHover
				? () => {
						hoverObservable.current = {};
				  }
				: undefined,
		[detectHover, hoverObservable],
	);

	// sharedRowData is passed to each rendered row via 'data' property. This is an optimization over using React's context API.
	const sharedRowData = useDeepMemoize({
		scrollXObservable,
		columnsObservable,
		hoverObservable,
		$render, // required to trigger manual update
		contentWidthObservable,
		rowRenderer,
		getRowKeyByIdx,
		onRowClick,
		onClick,
		onDoubleClick,
		onMouseDown,
		context,
		rowHover,
		border: rowBorders,
		background: rowBackground,
		cellBorders,
		striped,
	});

	//--------------------------------------------------------------

	useImperativeHandle(
		ref,
		() => ({
			getIndexAt, // Y => row index at Y
			getRowPosition, // row index => Y
			resetRowHeights, // Recalculate row heights
			scrollToItem, // Scroll to row by index
			detachScroll,
			attachScroll,
			getElementDetails,
			//getTotalRowsHeight, // Get total height of all rows (for auto-height)
		}),
		[
			attachScroll,
			detachScroll,
			getElementDetails,
			getIndexAt,
			getRowPosition,
			resetRowHeights,
			scrollToItem,
		],
	);

	//--------------------------------------------------------------

	if (debug) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useDebugger('Grid', {
			// props --------------
			getRowKeyByIdx,
			rowsCount,
			rowHeight,
			rowHover,
			columns,
			rowRenderer,
			forceUpdateIdx,
			onRangeChange,
			onRowClick,
			onRowDoubleClick,
			onRowMouseDown,
			context,
			overscanCount,
			rowBorders,
			cellBorders,
			rowBackground,
			striped,
			dragTooltipProp,
			onDragStartProp,
			onDragProp,
			onDragEndProp,
			background,
			detectHover,
			showHeader,
			store,
			outline,
			// state -------------
			getRowPosition,
			itemSize,
			resetRowHeights,
			getIndexAt,
			rowPositions,
			isVariableSize,
			totalHeight,
			updateCellWidths,
			$render,
			forceRender,
			columnsObservable,
			onClick,
			onDoubleClick,
			onMouseDown,
			sharedRowData,
			onRangeChangeThrottled,
			onRowsRendered,
			List,
			scrollToItem,
			onScrollUpdate,
			getIdxRange,
			onDragStart,
			onDrag,
			onDragEnd,
			dragTooltip,
			outerElementType,
			detachScroll,
			attachScroll,
			innerElementType,
			getElementDetails,
			onMouseOver,
			onMouseLeave,
		});
	}

	return (
		<div
			data-tag={tag}
			className={clsx(styles.Grid, background && styles.withBackground)}
			onMouseOver={onMouseOver}
			onMouseLeave={onMouseLeave}
		>
			{showHeader && (
				<Header
					hasScrollY={false /*hasScrollY*/} /*todo*/
					outline={outline}
					columnsObservable={columnsObservable}
					scrollXObservable={scrollXObservable}
					store={store}
					context={context}
				/>
			)}
			<div
				role={'rowgroup'}
				data-tag='grid-content-outline'
				style={{
					borderWidth: outline ? (showHeader ? '0 1px 1px' : 1) : 0,
				}}
				className={styles.contentOutline}
			>
				<VirtualizedSizer
					totalHeightObservable={totalHeightObservable}
					totalWidthObservable={totalWidthObservable}
					onSize={({ width, height }) => {
						availableWidthObservable.current = width;
						availableHeightObservable.current = height;
					}}
					style={{ flex: 1 }}
				>
					{({ width, height }) =>
						message ? (
							<div className={styles.message} style={{ width, height }}>
								{message}
							</div>
						) : (
							<List
								ref={listRef}
								innerRef={innerRef}
								outerElementType={outerElementType}
								innerElementType={innerElementType}
								width={width}
								height={height}
								itemCount={rowsCount}
								itemSize={itemSize}
								itemKey={getRowKeyByIdx}
								estimatedItemSize={0}
								itemData={sharedRowData}
								forceUpdateIdx={forceUpdateIdx}
								onItemsRendered={onRowsRendered}
								overscanCount={overscanCount}
								useIsScrolling={true}
							>
								{Row}
							</List>
						)
					}
				</VirtualizedSizer>
			</div>
		</div>
	);
};

Grid = forwardRef(Grid);
Grid = memo(Grid);
Grid.propTypes = {
	height: PropTypes.number,
	width: PropTypes.number,
	outerElementType: PropTypes.elementType,
	innerElementType: PropTypes.elementType,
	rowsCount: PropTypes.number,
	rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
	getRowKeyByIdx: PropTypes.func,
	rowRenderer: PropTypes.func,
	forceUpdateIdx: PropTypes.number,
	onRangeChange: PropTypes.func,
	onRowClick: PropTypes.func,
	onRowDoubleClick: PropTypes.func,
	onRowMouseDown: PropTypes.func,
	context: PropTypes.object,
	overscanCount: PropTypes.number,
	rowHover: PropTypes.bool,
	rowBorders: PropTypes.bool,
	cellBorders: PropTypes.bool,
	rowBackground: PropTypes.bool,
	striped: PropTypes.bool,
	debug: PropTypes.bool,
};

export { Grid };
